import React from 'react';

export const SectionEyebrow = ({ children }) => (
  <div className="tracking-[0.32em] text-[10.5px] uppercase text-neutral-500">{children}</div>
);

export const SectionTitle = ({ children, className = '' }) => (
  <h2 className={`font-serif text-[34px] md:text-[52px] leading-[1.05] tracking-[-0.01em] text-neutral-900 ${className}`}>
    {children}
  </h2>
);

export const SectionLead = ({ children, className = '' }) => (
  <p className={`text-neutral-600 text-[15px] md:text-[17px] leading-[1.75] font-light ${className}`}>
    {children}
  </p>
);

export const GhostLink = ({ children, href = '#', onClick }) => (
  <a
    href={href}
    onClick={onClick}
    className="inline-flex items-center gap-3 tracking-[0.24em] text-[11px] uppercase text-neutral-900 hover:text-neutral-500 transition-colors"
  >
    <span className="inline-block w-8 h-px bg-current" />
    {children}
  </a>
);

const revealCallbacks = new WeakMap();
let revealObserver;

const getRevealObserver = () => {
  if (!revealObserver) {
    revealObserver = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const callback = revealCallbacks.get(entry.target);
        if (callback) callback();
        revealCallbacks.delete(entry.target);
        revealObserver.unobserve(entry.target);
      }),
      { threshold: 0.15 }
    );
  }

  return revealObserver;
};

export const Reveal = ({ children, className = '' }) => {
  const ref = React.useRef(null);
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setVisible(true);
      return;
    }

    const observer = getRevealObserver();
    revealCallbacks.set(el, () => setVisible(true));
    observer.observe(el);
    return () => {
      revealCallbacks.delete(el);
      observer.unobserve(el);
    };
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
    >
      {children}
    </div>
  );
};
